/* eslint-disable comma-dangle */
/* eslint-disable max-len */
import { RouteConfig } from 'vue-router';
import { ModelType } from '@llws/platform-shared-types/types/db/auth';
import { CurrentUser, ERouteMetaPermissionMode, IRouteConfigAuthMeta, IRouteConfigMeta } from '@/shared';
import { IRouteConfigWithMeta } from '@/shared/interfaces/route-group';
import { getRouteMetaTitle, getTranslatedText } from '@/shared/helpers/route-meta-translations';
import app from '@/main';
import store from '@/store';

const standardListingsAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{
    key: 'standard_listings',
    keyType: ModelType.building,
    mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
  }],
};

const standardListingsEditAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{
    key: 'standard_listings',
    keyType: ModelType.building,
    mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
    accessType: 'update',
  }],
};

const connectionsAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{
    key: 'connections',
    keyType: ModelType.building,
    mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
  }],
};

const promotedListingAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{
    key: 'standard_listings',
    keyType: ModelType.building,
    mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
  },
  {
    key: 'promoted_listings',
    keyType: ModelType.building,
    mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
  }],
};

const syndicationGrid: RouteConfig = {
  path: '/syndication',
  name: 'AdSyndicationGrid',
  redirect: '/syndication/standard-listings',
  component: () => import('@/views/syndication/Index.vue'),
  meta: {
    auth: standardListingsAuth,
    title: 'Syndication',
    icon: 'sync-alt',
    active: false,
    noPagePadding: true,
    infoUrl: 'y',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: false,
    },
  },
};

const standardListings: RouteConfig = {
  path: '/syndication/standard-listings',
  name: 'AdSyndicationStandardListings',
  component: () => import('@/views/syndication/Index.vue'),
  meta: {
    auth: standardListingsAuth,
    title: 'Standard Listings',
    titleKey: 'listingManagement.nav.standardListings',
    icon: 'sync-alt',
    active: false,
    noPagePadding: true,
    infoUrl: 'y',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: false,
    },
    parent: syndicationGrid,
    breadcrumbs: (vm) => ([
      {
        to: '/syndication/standard-listings',
        title: getTranslatedText(vm, 'Listing Management', 'listingManagement.nav.listingManagement'),
      },
      {
        to: '/syndication/standard-listings',
        title: getTranslatedText(vm, 'Standard Listings', 'listingManagement.nav.standardListings'),
      }
    ]),
  },
};

const promotedListings: RouteConfig = {
  path: '/syndication/promoted-listings',
  name: 'AdSyndicationPromotedListings',
  component: () => import('@/views/syndication/PromotedListingsV2.vue'),
  meta: {
    auth: promotedListingAuth,
    title: 'Promoted Listings',
    titleKey: 'listingManagement.nav.promotedListings',
    icon: 'sync-alt',
    active: false,
    noPagePadding: true,
    infoUrl: 'y',
    footerBar: {
      enabled: true,
      hasSave: true,
    },
    parent: syndicationGrid,
    breadcrumbs: (vm) => ([
      {
        to: '/syndication/standard-listings',
        title: getTranslatedText(vm, 'Listing Management', 'listingManagement.nav.listingManagement'),
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm?.$route),
      },
    ]),
    dynamicHideFromNav: () => app()?.$rsGlobals.$hasPromotedListingsRestricted(),
  },
};

const connections: RouteConfig = {
  path: '/syndication/connections',
  name: 'AdSyndicationConnections',
  component: () => import('@/views/syndication/connections/Index.vue'),
  meta: {
    auth: connectionsAuth,
    title: 'Connections',
    titleKey: 'listingManagement.nav.connections',
    pageTitle: 'Ad Syndication - Connections',
    icon: 'code-merge',
    active: false,
    noPagePadding: true,
    infoUrl: 'y',
    parent: syndicationGrid,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
    overlayScrollbarsOptions: {
      overflowBehavior: {
        x: 'hidden'
      }
    },
    breadcrumbs: (vm) => ([
      {
        to: '/syndication/standard-listings',
        title: getTranslatedText(vm, 'Listing Management', 'listingManagement.nav.listingManagement'),
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm?.$route),
      },
    ]),
  },
};

const connectionSingle: RouteConfig = {
  path: '/syndication/connections/:partnerId/details',
  name: 'AdSyndicationConnectionSingle',
  component: () => import(
    /* webpackChunkName: "group-syndication" */
    '@/views/syndication/connections/single/Index.vue'
  ),
  meta: {
    auth: connectionsAuth,
    parent: connections,
    title: 'Connection Details',
    titleKey: 'listingManagement.nav.connectionDetailsTitle',
    icon: 'sync-alt',
    hidePageTitle: true,
    noHeader: true,
    active: false,
    noPagePadding: true,
    infoUrl: 'y',
    backButtonTarget: () => (app()?.$rsGlobals.$isIFrame ? { isCustom: false, target: connections } : null),
    breadcrumbs: (vm) => ([
      {
        to: '/syndication/standard-listings',
        title: getTranslatedText(vm, 'Listing Management', 'listingManagement.nav.listingManagement'),
      },
      {
        to: '/syndication/connections',
        title: getTranslatedText(vm, 'Connections', 'listingManagement.nav.connections'),
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: getRouteMetaTitle(vm, vm?.$route),
      },
    ]),
  },
};

const adLayouts: RouteConfig = {
  path: '/syndication/ad-layouts',
  name: 'AdSyndicationAdLayouts',
  component: () => import('@/views/syndication/AdLayouts.vue'),
  meta: {
    auth: standardListingsEditAuth,
    title: 'Layouts',
    titleKey: 'listingManagement.nav.layouts',
    pageTitle: 'Layouts',
    active: false,
    icon: 'browser',
    disabled: () => {
      const vm = app();
      // @ts-ignore
      if (vm) return vm.$rsGlobals.$isAdLayoutsDisabled ? { tooltip: vm.$ts('listingManagement.common.contactManagerToEnable') } : undefined;
      return undefined;
    },
    breadcrumbs: (vm) => ([
      {
        to: '/syndication/standard-listings',
        title: getTranslatedText(vm, 'Listing Management', 'listingManagement.nav.listingManagement'),
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm?.$route),
      },
    ]),
  },
};

const syndicationGeneralSettings: RouteConfig = {
  path: '/syndication/settings/general',
  name: 'AdSyndicationGeneralSettings',
  meta: {
    auth: standardListingsAuth,
    title: 'General Settings',
    titleKey: 'listingManagement.settings.routes.generalTitle',
    subtitle: 'Update Connection preferences',
    subtitleKey: 'listingManagement.settings.routes.updateConnectionPrefs',
    noPagePadding: false,
    active: false,
    icon: 'cogs',
    children: [],
    breadcrumbs: (vm) => ([
      {
        to: '/syndication/standard-listings',
        title: getTranslatedText(vm, 'Listing Management', 'listingManagement.nav.listingManagement'),
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm?.$route),
      },
    ]),
    footerBar: {
      enabled: true,
      hasSave: true,
    },
  },
  component: () => import('@/views/syndication/settings/GeneralSettings.vue'),
  props: true,
};

const syndicationPartnerSettings: RouteConfig = {
  path: '/syndication/settings/partner/:partnerKey',
  name: 'AdSyndicationPartnerSettings',
  meta: {
    auth: standardListingsAuth,
    title: 'Syndication Settings',
    titleKey: 'listingManagement.settings.routes.syndicationTitle',
    subtitle: 'Configure specific settings for your active Rental Listing Sites',
    subtitleKey: 'listingManagement.settings.routes.configureSpecific',
    noPagePadding: false,
    active: false,
    icon: 'cogs',
    children: [],
    breadcrumbs: (vm) => ([
      {
        to: '/syndication/standard-listings',
        title: getTranslatedText(vm, 'Listing Management', 'listingManagement.nav.listingManagement'),
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm?.$route),
      },
    ]),
    footerBar: {
      enabled: true,
      hasSave: true,
    },
  },
  component: () => import('@/views/syndication/settings/partner/Index.vue'),
  props: true,
};

const syndicationAutoResponderSettings: RouteConfig = {
  path: '/syndication/auto-responders',
  name: 'AdSyndicationAutoResponderSettings',
  meta: {
    auth: standardListingsAuth,
    title: 'Auto Responders',
    titleKey: 'listingManagement.nav.autoResponders',
    noPagePadding: false,
    active: false,
    icon: 'paper-plane',
    children: [],
    disabled: () => {
      const vm = app();
      // @ts-ignore
      if (vm) return vm.$rsGlobals.$isAutoRespondersDisabled ? { tooltip: vm.$ts('listingManagement.common.contactManagerToEnable') } : undefined;
      return undefined;
    },
    breadcrumbs: (vm) => ([
      {
        to: '/syndication/standard-listings',
        title: getTranslatedText(vm, 'Listing Management', 'listingManagement.nav.listingManagement'),
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm?.$route),
      },
    ]),
  },
  component: () => import('@/views/syndication/AutoResponders.vue'),
  props: true,
};

const syndicationOutboundIntegrationSettings: RouteConfig = {
  path: '/syndication/outbound-integrations',
  name: 'AdSyndicationSettingsOutboundIntegrations',
  meta: {
    auth: standardListingsAuth,
    title: 'Integrations',
    titleKey: 'listingManagement.nav.integrations',
    noPagePadding: false,
    active: false,
    icon: 'exchange',
    children: [],
    parent: null,
    disabled: () => {
      const vm = app();
      // @ts-ignore
      if (vm) return vm.$rsGlobals.$isWebhooksDisabled ? { tooltip: vm.$ts('listingManagement.common.contactManagerToEnable') } : undefined;
      return undefined;
    },
    breadcrumbs: (vm) => ([
      {
        to: '/syndication/standard-listings',
        title: getTranslatedText(vm, 'Listing Management', 'listingManagement.nav.listingManagement'),
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm?.$route),
      },
    ]),
  },
  component: () => import('@/views/syndication/integrations/WebhookIntegrations.vue'),
  props: true,
};

const settings: RouteConfig = {
  path: '/syndication/settings',
  name: 'AdSyndicationSettings',
  redirect: '/syndication/settings/general',
  component: () => import('@/views/syndication/settings/Index.vue'),
  children: [],
  meta: {
    auth: standardListingsEditAuth,
    title: 'Settings',
    titleKey: 'listingManagement.settings.routes.settingsTitle',
    pageTitle: 'Ad Syndication - Settings',
    subtitle: 'Update Connection preferences',
    subtitleKey: 'listingManagement.settings.routes.updateConnectionPrefs',
    icon: 'sync-alt',
    active: false,
    breadcrumbs: (vm) => ([
      {
        to: '/syndication/standard-listings',
        title: getTranslatedText(vm, 'Listing Management', 'listingManagement.nav.listingManagement'),
      },
      {
        to: vm?.$route.path ?? '',
        title: getRouteMetaTitle(vm, vm?.$route),
      },
    ]),
  },
  props: true,
};

const settingsChildren: RouteConfig[] = [
  {
    ...syndicationGeneralSettings,
    ...{
      name: 'AdSyndicationGeneralSettingsChild',
      meta: { ...syndicationGeneralSettings.meta!, parent: settings },
    }
  },
  {
    ...syndicationPartnerSettings,
    ...{
      name: 'AdSyndicationPartnerSettingsChild',
      meta: { ...syndicationPartnerSettings.meta!, parent: settings },
    }
  },
];

settings.children = settingsChildren;

export default [
  syndicationGrid,
  standardListings,
  promotedListings,
  connections,
  connectionSingle,
  adLayouts,
  syndicationAutoResponderSettings,
  syndicationOutboundIntegrationSettings,
  settings,
];
